import React, { useState, useEffect, useRef } from 'react';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
    FaLock,
    FaCheckCircle,
    FaUser, FaPhoneAlt, FaMapMarkerAlt, FaInfoCircle, FaSyncAlt, FaTimesCircle
} from 'react-icons/fa';
import apiClient from '../../api/client';
import logoGP from '../../assets/images/GP.png';

const IS_KATEKISAN_OPTIONS = [
    { value: 0, label: 'GP' },
    { value: 1, label: 'Katekisan' },
    { value: 2, label: 'Simpatisan' },
];

const FORCE_OPEN = false;
const API_KEY = 'gpibkkbbmantap2023';

const checkAbsensiStatus = () => {
    const now = new Date();
    const targetDay = 5; // Jumat
    const openHour = 19; // 19:00 WITA
    const closeHour = 22; // 22:00 WITA


    const currentDay = now.getDay();
    const currentHour = now.getHours();

    // Logika Buka: Jika hari Jumat DAN jam di antara 19 sampai 21:59
    let isOpen = FORCE_OPEN ? true : (currentDay === targetDay && currentHour >= openHour && currentHour < closeHour);

    // Hitung tanggal Jumat depan untuk keterangan "Jadwal Berikutnya"
    let targetTimeNext = new Date(now);
    targetTimeNext.setHours(openHour, 0, 0, 0);

    let daysUntilFriday = (targetDay + 7 - now.getDay()) % 7;
    // Jika hari ini Jumat tapi sudah lewat jam 22, tunjukkan Jumat minggu depan
    if (daysUntilFriday === 0 && currentHour >= closeHour) {
        daysUntilFriday = 7;
    }
    targetTimeNext.setDate(now.getDate() + daysUntilFriday);


    const formatter = new Intl.DateTimeFormat('id-ID', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return {
        isOpen,
        openTime: '19:00',
        closeTime: '22:00',
        nextOpeningDate: formatter.format(targetTimeNext),

    };

};

const humanizeErrorMessage = (rawMessage) => {
    if (!rawMessage) return 'Gagal mengirim absensi. Silakan coba lagi.';

    const msg = String(rawMessage).toLowerCase();

    // Deteksi berbagai kemungkinan format pesan "nama sudah ada / duplikat" dari backend
    if (
        msg.includes('duplicate') ||
        msg.includes('unique') ||
        msg.includes('already exist') ||
        msg.includes('sudah terdaftar') ||
        msg.includes('sudah ada') ||
        msg.includes('sudah absen')
    ) {
        return 'Nama Kamu sudah ada di database untuk hari ini!';
    }

    if (msg.includes('network') || msg.includes('timeout') || msg.includes('fetch')) {
        return 'Koneksi bermasalah. Periksa internet kamu dan coba lagi.';
    }

    if (msg.includes('sektor') || msg.includes('sector')) {
        return 'Sektor tidak valid atau gagal dimuat. Silakan refresh halaman.';
    }

    // Fallback: tampilkan pesan asli dari server apa adanya
    return rawMessage;
};

const ClosedFormMessage = ({ nextOpeningDate }) => (
    <div className="text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <FaLock className="text-4xl text-red-500" />
        </div>
        <h2 className="text-2xl text-gray-800 font-black mb-2 tracking-tight">
            ABSENSI DITUTUP
        </h2>
        <p className="text-gray-500 mb-8 max-w-xs mx-auto text-sm">
            Formulir otomatis tersedia setiap hari Jumat saat jam ibadah Pelkat GP.
        </p>
        <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-2xl border border-red-100 shadow-sm">
            <p className="text-[15px] uppercase tracking-[0.2em] text-red-600 font-bold mb-2">Jadwal Berikutnya</p>
            <p className="text-2xl font-extrabold text-red-800 tracking-wide">{nextOpeningDate}</p>
            <p className="text-sm font-medium text-red-700 mt-2 italic">Pukul 19:00 WITA</p>
        </div>
    </div>

);


const AbsensiGP = () => {
    const [absensiStatus] = useState(checkAbsensiStatus());
    const [availableSectors, setAvailableSectors] = useState([]);
    const [namaLengkap, setNamalengkap] = useState('');
    const [nomorTelepon, setNomorTelepon] = useState('');
    const [selectedSektorId, setSelectedSektorId] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isErrorPage, setIsErrorPage] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState(0);

    const isSubmittingRef = useRef(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        const fetchSectors = async () => {
            try {
                const data = await apiClient.getSektor();
                if (data && Array.isArray(data)) {
                    const mappedSectors = data
                        .map(sektor => ({
                            // Mengambil langsung dari properti 'code' hasil API
                            id: sektor.id,
                            code: sektor.code,
                            nama: sektor.sektor
                        }))
                        .filter(sektor =>
                            // Tetap menyaring kode '00' dan nama 'GENERAL'
                            sektor.code !== '00' &&
                            sektor.nama.toUpperCase() !== 'GENERAL'
                        )
                        .sort((a, b) => a.nama.localeCompare(b.nama));
                    setAvailableSectors(mappedSectors);
                }
            } catch (error) {
                setSubmissionStatus({ type: 'error', message: 'Gagal memuat data sektor.' });
            }
        };
        fetchSectors();
    }, []);

    const handleResetForm = () => {
        setNamalengkap('');
        setNomorTelepon('');
        setSelectedSektorId('');
        setSelectedStatus(null);
        setIsSuccess(false);
        setIsErrorPage(false);
        setSubmissionStatus(null);
    };

    const handleBackFromError = () => {
        isSubmittingRef.current = false;
        setIsErrorPage(false);
        setSubmissionStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmittingRef.current) return;

        // 1. Persiapan data awal (untuk divalidasi)
        const sektorTerpilih = availableSectors.find(s => s.id === selectedSektorId);
        const cleanedPhone = nomorTelepon.replace(/[^0-9]/g, ''); // Hapus karakter non-angka

        // 2. LOGIKA VALIDASI
        // Cek Nama Lengkap
        const nameRegex = /^[a-zA-Z\s]+$/;

        if (!namaLengkap || namaLengkap.trim().length < 3) {
            setSubmissionStatus({ type: 'error', message: 'Nama lengkap minimal 3 karakter!' });
            return;
        }

        if (!nameRegex.test(namaLengkap)) {
            setSubmissionStatus({
                type: 'error',
                message: 'Nama lengkap hanya boleh berisi huruf (tidak boleh angka atau simbol)!'
            });
            return;
        }

        // Cek Nomor Telepon (Hanya angka, minimal 10 digit)
        const phoneRegex = /^[0-9]+$/;
        if (!cleanedPhone || !phoneRegex.test(cleanedPhone) || cleanedPhone.length < 10) {
            setSubmissionStatus({
                type: 'error',
                message: 'Nomor telepon harus angka dan minimal 10 digit!'
            });
            return;
        }

        // Cek Status Jemaat (0, 1, atau 2)
        if (selectedStatus === null || selectedStatus === undefined) {
            setSubmissionStatus({ type: 'error', message: 'Silakan pilih Status Jemaat terlebih dahulu!' });
            return;
        }

        // 3. JIKA LOLOS VALIDASI, MULAI PROSES KIRIM
        const submissionData = {
            api_key: API_KEY,
            id_sektor: selectedSektorId,
            sektor: sektorTerpilih ? sektorTerpilih.nama : '',
            nama_lengkap: namaLengkap,
            nomor_telepon: cleanedPhone,
            is_katekisan: parseInt(selectedStatus, 10),
        };

        isSubmittingRef.current = true;
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        setIsSubmitting(true);
        setLoadingProgress(0);
        setSubmissionStatus(null);

        const MIN_LOADING_TIME = 1000;
        const startTime = Date.now();

        const progressInterval = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
            if (calculatedProgress > 99) calculatedProgress = 99;
            setLoadingProgress(Math.floor(calculatedProgress));
        }, 50);

        const finishAfterMinLoading = (applyResult) => {
            const actualTimeTaken = Date.now() - startTime;
            const remainingTime = Math.max(0, MIN_LOADING_TIME - actualTimeTaken);
            setTimeout(() => {
                clearInterval(progressInterval);
                setLoadingProgress(100);
                setTimeout(() => {
                    if (currentRequestId !== requestIdRef.current) return;
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    applyResult();
                }, 500);
            }, remainingTime);
        };

        try {
            // console.log("Mengirim data ke API...", submissionData);

            await apiClient.createAbsenGp(submissionData);

            finishAfterMinLoading(() => {
                setIsSuccess(true);
                setIsErrorPage(false);
            });

            const actualTimeTaken = Date.now() - startTime;
            const remainingTime = Math.max(0, MIN_LOADING_TIME - actualTimeTaken);

            setTimeout(() => {
                clearInterval(progressInterval);
                setLoadingProgress(100);

                setTimeout(() => {
                    setIsSubmitting(false);
                    setIsSuccess(true);
                }, 700);
            }, remainingTime);

        } catch (error) {
            clearInterval(progressInterval);

            const rawErrorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                'Gagal mengirim absensi.';

            const errorMsg = humanizeErrorMessage(rawErrorMsg);

            finishAfterMinLoading(() => {
                setSubmissionStatus({ type: 'error', message: errorMsg });
                setIsErrorPage(true);
                setIsSuccess(false);
            });
        }
    };

    const inputClass = "w-full px-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all duration-200 shadow-inner text-gray-700 font-medium";
    const labelClass = "flex items-center gap-2 text-gray-600 font-bold text-xs uppercase tracking-wider ml-1 mb-2";

    return (
        <PageTransitionWrapper>
            <div className="bg-[#f8fafc] min-h-screen py-10 px-4">
                <div className="max-w-lg mx-auto">

                    {/* HEADER */}
                    <div className="bg-white rounded-[2rem] shadow-xl shadow-blue-900/5 p-8 mb-8 flex flex-col items-center text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 via-blue-600 to-indigo-600" />
                        <img src={logoGP} alt="Logo" className="w-20 h-20 object-contain mb-4 drop-shadow-md" />
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-tight uppercase">
                            Absensi Ibadah <span className="text-blue-600">GP</span>
                        </h1>
                        <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-[0.15em]">GPIB Kasih Karunia Badung</p>
                    </div>

                    <div className="bg-white rounded-[2rem] shadow-2xl p-8 border border-gray-50 relative min-h-[400px] flex flex-col justify-center">

                        {/* 1. STATE LOADING (SESUAI FOTO) */}
                        {isSubmitting && (
                            <LoadingSpinner
                                progress={loadingProgress}
                                text="Mengirim Absensi..."
                            />
                        )}

                        {/* 2. STATE SUKSES (SESUAI REQUEST) */}
                        {isSuccess && !isSubmitting && !isErrorPage && (
                            <div className="text-center animate-in slide-in-from-bottom-4 duration-500">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaCheckCircle className="text-5xl text-green-500" />
                                </div>
                                <h2 className="text-2xl font-black text-gray-900 leading-tight mb-4">
                                    Absensi Sudah Terkirim!
                                </h2>
                                <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 mb-8">
                                    <p className="text-green-800 font-bold italic leading-relaxed">
                                        "Selamat Menjalani Ibadah, <br />Tuhan Yesus Memberkati!"
                                    </p>
                                </div>

                                {/* Tombol Aksi */}
                                <div className="grid grid-cols-1">
                                    <button
                                        onClick={handleResetForm}
                                        className="flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg active:scale-95"
                                    >
                                        <FaSyncAlt size={14} /> KEMBALI
                                    </button>
                                </div>
                            </div>
                        )}
                        {/* 3. STATE ERROR */}
                        {isErrorPage && !isSubmitting && !isSuccess && (
                            <div className="text-center animate-in slide-in-from-bottom-4 duration-500">
                                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaTimesCircle className="text-5xl text-red-500" />
                                </div>
                                <h2 className="text-2xl font-black text-gray-900 leading-tight mb-2">Gagal Absen!</h2>
                                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-6">Data Tidak Dapat Tersimpan</p>
                                <div className="bg-red-50 p-5 rounded-2xl border border-red-100 mb-8">
                                    <p className="text-red-800 font-bold">
                                        {submissionStatus?.message}
                                    </p>
                                </div>
                                <button onClick={handleBackFromError} className="w-full flex items-center justify-center gap-2 py-3.5 bg-gray-800 text-white rounded-2xl font-bold hover:bg-gray-900 transition-all shadow-lg active:scale-95">
                                    <FaSyncAlt size={14} /> PERBAIKI DATA / KEMBALI
                                </button>
                            </div>
                        )}


                        {/* 4. FORM UTAMA */}
                        {!isSubmitting && !isSuccess && !isErrorPage && absensiStatus.isOpen && (
                            <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in duration-500">

                                <div className="mb-6 p-5 bg-blue-50 border border-blue-100 rounded-[1.5rem] text-center">
                                    <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] mb-1">
                                        Jadwal Absensi
                                    </h4>
                                    <p className="text-1xl font-bold text-blue-900">
                                        Jumat <span className="mx-2 text-blue-200">|</span> 19:00 — 22:00 WITA
                                    </p>
                                    <p className="text-[10px] text-blue-400 italic font-medium mt-1 uppercase">
                                        Mohon mengisi absen tepat waktu
                                    </p>
                                </div>

                                {submissionStatus && (
                                    <div className="p-4 bg-red-50 border border-red-100 text-red-700 rounded-2xl text-xs font-bold flex items-center gap-2">
                                        <FaInfoCircle /> {submissionStatus.message}
                                    </div>
                                )}

                                <div>
                                    <label className={labelClass}><FaMapMarkerAlt className="text-blue-500" /> Pilih Sektor</label>
                                    <div className="relative">
                                        <select value={selectedSektorId} onChange={(e) => setSelectedSektorId(e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required>
                                            <option value="" disabled>Klik untuk memilih...</option>
                                            {availableSectors.map(s => (
                                                <option key={s.id} value={s.id}> {/* s.id di sini adalah UUID/Angka asli dari API */}
                                                    {s.nama}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}><FaUser className="text-blue-500" /> Nama Lengkap</label>
                                    <input type="text" className={inputClass} placeholder="Masukkan nama panjang" value={namaLengkap} onChange={e => setNamalengkap(e.target.value)} required />
                                </div>

                                <div>
                                    <label className={labelClass}><FaPhoneAlt className="text-blue-500" /> No. HP (WhatsApp)</label>
                                    <input type="text" className={inputClass} placeholder="0812XXXXXXXX" value={nomorTelepon} onChange={e => setNomorTelepon(e.target.value)} required />
                                </div>

                                <div>
                                    <label className={labelClass}>Status Jemaat</label>
                                    <div className="grid grid-cols-3 gap-2 p-1.5 bg-gray-100 rounded-2xl border border-gray-200">
                                        {IS_KATEKISAN_OPTIONS.map((opt) => (
                                            <label key={opt.value} className={`flex items-center justify-center py-3 rounded-xl cursor-pointer transition-all duration-300 text-[13px] font-black uppercase tracking-wider ${selectedStatus === opt.value ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg shadow-blue-200 scale-105 ring-2 ring-white' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200/50'}`}>
                                                <input type="radio" className="hidden" name="status" checked={selectedStatus === opt.value} onChange={() => setSelectedStatus(opt.value)} />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <button type="submit" disabled={availableSectors.length === 0} className="w-full py-4 rounded-2xl font-black text-white text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:shadow-blue-500/25 transition-all shadow-lg active:scale-[0.98]">
                                    Kirim Absensi Sekarang
                                </button>
                            </form>
                        )}

                        {/* 4. FORM TERTUTUP */}
                        {!absensiStatus.isOpen && !isSubmitting && !isSuccess && (
                            <ClosedFormMessage nextOpeningDate={absensiStatus.nextOpeningDate} />
                        )}
                    </div>
                </div>
            </div>
        </PageTransitionWrapper>
    );
};

export default AbsensiGP;